/* eslint-disable @typescript-eslint/no-unused-vars */
import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { useCustomerPageContext } from "@contexts/customer"
import { updateCustomerProducts } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack, Typography } from "@mui/material"
import { showErrorToast } from "@utils/toast"
import { LimitsAndUsages } from "__generated__/graphql"
import { useEffect, useState } from "react"

export default function PlanLimitsAndUsages({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, currentCustomer: customer } = useCustomerPageContext()

  const [loading, setLoading] = useState(false)

  const [limits, setLimits] = useState<LimitsAndUsages>()
  const [prevLimits, setPrevLimits] = useState<LimitsAndUsages>()

  const [usages, setUsages] = useState<LimitsAndUsages>()
  const [prevUsages, setPrevUsages] = useState<LimitsAndUsages>()

  useEffect(() => {
    if (customer?.package?.limits) {
      /* eslint-disable-next-line no-unsafe-optional-chaining */
      const { __typename, ...filteredUsages } = customer?.package.limits
      setLimits(filteredUsages)
      setPrevLimits(filteredUsages)
    }
  }, [customer])

  useEffect(() => {
    if (customer?.package?.usages) {
      /* eslint-disable-next-line no-unsafe-optional-chaining */
      const { __typename, ...filteredUsages } = customer?.package.usages
      setUsages(filteredUsages)
      setPrevUsages(filteredUsages)
    }
  }, [customer])

  const { control } = useControlledForm()

  function handleClose() {
    if (loading) {
      return
    }
    setClose()
  }

  async function handleSubmit() {
    if (!customer?.package?._id) {
      return
    }
    setLoading(true)
    const { isSuccess, errorMessage } = await updateCustomerProducts({
      _id: customer?.package?._id,
      limits: limits,
      usages: usages
    })
    if (!isSuccess && !!errorMessage) {
      showErrorToast(errorMessage)
      setLoading(false)
      return
    }
    setLoading(false)
    handleClose()
  }

  const handleLimitChange = (key: string, newValue: number) => {
    setLimits((prevLimits) => ({
      ...prevLimits,
      [key]: newValue
    }))
  }

  const handleUsageChange = (key: string, newValue: number) => {
    setUsages((prevUsages) => ({
      ...prevUsages,
      [key]: newValue
    }))
  }

  return (
    <DialogProvider
      title={translate("limits")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit,
        loading: loading,
        disabled:
          JSON.stringify(prevLimits) === JSON.stringify(limits) &&
          JSON.stringify(prevUsages) === JSON.stringify(usages)
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={2}>
        {limits ? (
          Object.entries(limits).map(([key, value], index) => (
            <Stack key={index}>
              <Typography>{key}</Typography>
              <Stack flexDirection={"row"} justifyContent={"space-between"}>
                <TextInput
                  type="number"
                  label={""}
                  disabled={loading}
                  name={`usage-${index}`}
                  placeholder="0"
                  control={control}
                  value={(usages as Record<string, number>)[key]}
                  onChange={(e) =>
                    handleUsageChange(key, parseFloat(e.target.value))
                  }
                />
                <TextInput
                  type="number"
                  label={""}
                  disabled={loading}
                  name={`limit-${index}`}
                  placeholder="0"
                  control={control}
                  value={value}
                  onChange={(e) =>
                    handleLimitChange(key, parseFloat(e.target.value))
                  }
                />
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography>{translate("no_usages_and_limits")}</Typography>
        )}
      </Stack>
    </DialogProvider>
  )
}
